import { orderApi } from "@api/acquisition";
import { documentRequestApi } from "@api/documentRequests";
import { withCancel } from "@api/utils";
import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { invenioConfig } from "@config";
import { RJSForm } from "@forms/rjsf";
import { goTo } from "@history";
import { AcquisitionRoutes, BackOfficeRoutes } from "@routes/urls";
import _get from "lodash/get";
import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Container,
  Form,
  Grid,
  Header,
  Icon,
  Label,
  Message,
  Segment,
  Modal,
  Button,
} from "semantic-ui-react";
import { schema } from "./schema";
import { uiSchema } from "./uiSchema";
import { AuditService } from "@api/audit/audit";
import { userApi } from "@api/users/user";

export class OrderEditor extends Component {
  constructor(props) {
    super(props);
    const isEditing = !!props.match.params.orderPid;
    this.state = {
      formData: {},
      originalFormData: {},
      isLoading: isEditing,
      error: {},
      selectedFiles: [],
      existingImages: [],
      uploadProgress: {},
      previewModalOpen: false,
      previewImageUrl: "",
      confirmDeleteModalOpen: false,
      imagePendingDelete: null,
    };
  }

  componentDidMount() {
    const {
      match: {
        params: { orderPid },
      },
    } = this.props;
    if (orderPid) {
      this.fetchOrder(orderPid);
    }
  }

  componentWillUnmount() {
    this.cancellableFetchOrder && this.cancellableFetchOrder.cancel();
    this.cancellableAttachOrder && this.cancellableAttachOrder.cancel();
  }

  fetchOrder = async (orderPid) => {
    this.cancellableFetchOrder = withCancel(orderApi.get(orderPid));
    try {
      const response = await this.cancellableFetchOrder.promise;
      const data = response.data;
      if (data.metadata && !data.metadata.order_lines) {
        data.metadata.order_lines = [];
      }

      this.setState({
        formData: data.metadata,
        originalFormData: JSON.parse(JSON.stringify(data.metadata)), // Deep copy for comparison
        isLoading: false,
        error: {},
        existingImages: Array.isArray(data.metadata.image_filenames)
          ? data.metadata.image_filenames
          : [],
      });
    } catch (error) {
      if (error !== "UNMOUNTED") {
        this.setState({ isLoading: false, error });
      }
    }
  };

  onFormChange = ({ formData }) => {
    this.setState({
      formData,
    });
  };

  // Helper function to compare and generate change details
  generateChangeDetails = (original, updated) => {
    const changes = [];

    // Get all possible fields from both objects
    const allFields = new Set([
      ...Object.keys(original || {}),
      ...Object.keys(updated || {}),
      "order_lines",
      "vendor",
      "status",
      "delivery_date",
      "total_price",
      "currency",
      "notes",
      "remarks",
      "recipient",
      "delivery_address",
      "order_type",
      "priority",
      "expected_delivery_date",
      "payment_method",
      "discount",
      "tax_amount",
      "shipping_cost",
    ]);

    allFields.forEach((field) => {
      const originalValue = this.getFieldValue(original, field);
      const updatedValue = this.getFieldValue(updated, field);

      if (this.hasFieldChanged(originalValue, updatedValue)) {
        const fieldLabel = this.getFieldLabel(field);
        let changeText;

        // Special handling for specific fields
        if (field === "image_filenames") {
          changeText = this.formatImageChange(originalValue, updatedValue);
        } else if (field === "document") {
          changeText = this.formatDocumentChange(originalValue, updatedValue);
          // Skip if no meaningful document changes
          if (changeText === null) return;
        } else {
          const originalDisplay = this.formatValueForDisplay(originalValue);
          const updatedDisplay = this.formatValueForDisplay(updatedValue);
          changeText = `${fieldLabel}: ${originalDisplay} -> ${updatedDisplay}`;
        }

        changes.push(changeText);
      }
    });

    return changes;
  };

  // Helper to get field value with nested support
  getFieldValue = (obj, field) => {
    // Handle case where obj is null/undefined
    if (!obj) return null;

    // For image_filenames, return the actual array for special processing
    if (field === "image_filenames") {
      return obj[field] || [];
    }

    // Special handling for order_lines - return actual array, not count
    if (field === "order_lines") {
      if (Array.isArray(obj[field])) {
        return obj[field]; // Return the actual array for comparison
      }
      return obj[field] || null;
    }

    // Handle nested fields (e.g., 'vendor.name')
    if (field.includes(".")) {
      const keys = field.split(".");
      let value = obj;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined) break;
      }
      return value;
    }

    // Return the actual value, preserving null/undefined/empty string differences
    return Object.prototype.hasOwnProperty.call(obj, field) ? obj[field] : null;
  };

  // Helper to check if field has changed
  hasFieldChanged = (original, updated) => {
    // Handle null/undefined cases
    if (original === null && updated === null) return false;
    if (original === undefined && updated === undefined) return false;
    if (
      (original === null || original === undefined) &&
      (updated === null || updated === undefined)
    )
      return false;

    // Convert empty strings to null for consistent comparison
    const normalizeValue = (val) => {
      if (val === "" || val === null || val === undefined) return null;
      return val;
    };

    const normalizedOriginal = normalizeValue(original);
    const normalizedUpdated = normalizeValue(updated);

    // If one is null and the other isn't, it's a change
    if (normalizedOriginal === null && normalizedUpdated !== null) return true;
    if (normalizedOriginal !== null && normalizedUpdated === null) return true;
    if (normalizedOriginal === null && normalizedUpdated === null) return false;

    // Handle arrays
    if (Array.isArray(normalizedOriginal) && Array.isArray(normalizedUpdated)) {
      if (normalizedOriginal.length !== normalizedUpdated.length) return true;
      return (
        JSON.stringify(normalizedOriginal) !== JSON.stringify(normalizedUpdated)
      );
    }

    // Special handling for document objects - compare by key fields only
    if (
      typeof normalizedOriginal === "object" &&
      typeof normalizedUpdated === "object"
    ) {
      // Check if these look like document objects
      if (normalizedOriginal.title && normalizedUpdated.title) {
        return this.hasDocumentChanged(normalizedOriginal, normalizedUpdated);
      }
      return (
        JSON.stringify(normalizedOriginal) !== JSON.stringify(normalizedUpdated)
      );
    }

    // Handle primitives - convert to string for comparison to avoid type issues
    return String(normalizedOriginal) !== String(normalizedUpdated);
  };

  // Special comparison for document objects
  hasDocumentChanged = (originalDoc, updatedDoc) => {
    // Compare only the key fields that matter for business logic
    const keyFields = [
      "title",
      "authors",
      "edition",
      "publication_year",
      "pid",
    ];

    for (const field of keyFields) {
      const originalValue = originalDoc[field];
      const updatedValue = updatedDoc[field];

      // Normalize values
      const normalizeDocValue = (val) =>
        val === undefined || val === "" ? null : val;

      if (
        normalizeDocValue(originalValue) !== normalizeDocValue(updatedValue)
      ) {
        return true;
      }
    }

    // Check cover_metadata ISBN only (ignore urls which might be added/removed automatically)
    const originalIsbn = originalDoc.cover_metadata?.isbn;
    const updatedIsbn = updatedDoc.cover_metadata?.isbn;
    if (originalIsbn !== updatedIsbn) {
      return true;
    }

    return false;
  };

  // Helper to get human-readable field labels
  getFieldLabel = (field) => {
    const labels = {
      order_lines: "Order Lines",
      vendor: "Vendor",
      status: "Status",
      delivery_date: "Delivery Date",
      total_price: "Total Price",
      currency: "Currency",
      notes: "Notes",
      remarks: "Remarks",
      recipient: "Recipient",
      delivery_address: "Delivery Address",
      order_type: "Order Type",
      priority: "Priority",
      expected_delivery_date: "Expected Delivery Date",
      payment_method: "Payment Method",
      discount: "Discount",
      tax_amount: "Tax Amount",
      shipping_cost: "Shipping Cost",
    };
    return (
      labels[field] ||
      field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, " ")
    );
  };

  // Helper to format values for display
  formatValueForDisplay = (value) => {
    if (value === null || value === undefined) {
      return "[Empty]";
    }
    if (value === "") {
      return "[Empty]";
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return "[No items]";
      }
      return `${value.length} items`;
    }
    if (typeof value === "object") {
      // Try to extract meaningful info from objects
      if (value.title) return value.title;
      if (value.name) return value.name;
      if (value.id) return value.id;
      if (value.value) return value.value;
      return "[Object]";
    }
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    if (typeof value === "number") {
      return String(value);
    }

    const stringValue = String(value);
    if (stringValue.length > 50) {
      return `${stringValue.substring(0, 47)}...`;
    }
    return stringValue;
  };

  // Special formatter for image changes
  formatImageChange = (originalImages, updatedImages) => {
    const originalCount = Array.isArray(originalImages)
      ? originalImages.length
      : 0;
    const updatedCount = Array.isArray(updatedImages)
      ? updatedImages.length
      : 0;

    if (originalCount === 0 && updatedCount > 0) {
      return `Images: None -> ${updatedCount} images added`;
    } else if (originalCount > 0 && updatedCount === 0) {
      return `Images: ${originalCount} images -> All images removed`;
    } else if (originalCount > updatedCount) {
      const removed = originalCount - updatedCount;
      return `Images: ${originalCount} -> ${updatedCount} images (${removed} removed)`;
    } else if (originalCount < updatedCount) {
      const added = updatedCount - originalCount;
      return `Images: ${originalCount} -> ${updatedCount} images (${added} added)`;
    } else {
      return `Images: ${originalCount} images (modified)`;
    }
  };

  // Special formatter for document changes
  formatDocumentChange = (originalDoc, updatedDoc) => {
    if (!originalDoc && !updatedDoc) return "Document: [No change]";
    if (!originalDoc)
      return `Document: None -> ${updatedDoc.title || "New Document"}`;
    if (!updatedDoc)
      return `Document: ${originalDoc.title || "Document"} -> None`;

    const changes = [];
    const keyFields = ["title", "authors", "edition", "publication_year"];

    for (const field of keyFields) {
      const originalValue = originalDoc[field] || "";
      const updatedValue = updatedDoc[field] || "";

      if (originalValue !== updatedValue) {
        const fieldName =
          field.charAt(0).toUpperCase() + field.slice(1).replace("_", " ");
        changes.push(`${fieldName}: "${originalValue}" -> "${updatedValue}"`);
      }
    }

    // Check ISBN changes
    const originalIsbn = originalDoc.cover_metadata?.isbn;
    const updatedIsbn = updatedDoc.cover_metadata?.isbn;
    if (originalIsbn !== updatedIsbn) {
      changes.push(`ISBN: "${originalIsbn || ""}" -> "${updatedIsbn || ""}"`);
    }

    if (changes.length === 0) {
      return null; // No meaningful changes
    }

    return `Document changed: ${changes.join(", ")}`;
  };

  // Audit logging function for order changes
  logOrderAudit = async (orderPid, changes, isCreating = false) => {
    try {
      const userResponse = await userApi.get();
      const currentUser = userResponse?.data || {
        username: "Unknown",
        full_name: "Unknown User",
      };

      let action, details; // Declare details in the right scope

      if (isCreating) {
        action = "Create Order";
        details = `User ${currentUser.full_name} created a new acquisition order (ID: ${orderPid}).`;
      } else {
        action = "Edit Order";
        if (changes.length > 0) {
          // Limit the details to prevent truncation issues
          const maxChanges = 10; // Limit number of changes shown
          const displayChanges = changes.slice(0, maxChanges);
          const changesList = displayChanges
            .map((change) => `* ${change}`)
            .join("\n");

          details = `User ${currentUser.full_name} edited acquisition order (ID: ${orderPid}):\n${changesList}`;

          // Add note if there were more changes
          if (changes.length > maxChanges) {
            details += `\n* ... and ${
              changes.length - maxChanges
            } more changes`;
          }

          // Ensure details don't exceed reasonable length (most DBs have limits)
          if (details.length > 4000) {
            details =
              details.substring(0, 3950) +
              "\n[Details truncated due to length]";
          }
        } else {
          details = `User ${currentUser.full_name} edited acquisition order (ID: ${orderPid}) - No field changes detected.`;
        }
      }

      // Force unique log creation with timestamp
      const timestamp = new Date().toISOString();
      const auditData = {
        action,
        details,
        created_by: `${currentUser.full_name}`,
        timestamp,
        order_id: orderPid,
        unique_id: `${orderPid}-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
      };

      const auditResponse = await AuditService.api.createLog(auditData);

      return auditResponse;
    } catch (auditError) {
      console.error(
        "Audit error details:",
        auditError.response?.data || auditError.message
      );
      throw auditError; // Re-throw to see if this affects the main flow
    }
  };

  handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const updatedFiles = [...this.state.selectedFiles, ...files];
    this.setState({ selectedFiles: updatedFiles });
  };

  removeSelectedFile = (indexToRemove) => {
    const updatedFiles = this.state.selectedFiles.filter(
      (_, index) => index !== indexToRemove
    );
    this.setState({ selectedFiles: updatedFiles });
  };

  removeAllExistingImages = () => {
    this.setState({ existingImages: [] });
  };

  removeExistingImage = () => {
    this.setState((prevState) => ({
      existingImages: prevState.existingImages.filter(
        (img) => img !== prevState.imagePendingDelete
      ),
      confirmDeleteModalOpen: false,
      imagePendingDelete: null,
    }));
  };

  openImagePreviewModal = (imageUrl) => {
    this.setState({
      previewImageUrl: imageUrl,
      previewModalOpen: true,
    });
  };

  openDeleteConfirmation = (imageName) => {
    this.setState({
      confirmDeleteModalOpen: true,
      imagePendingDelete: imageName,
    });
  };

  closeDeleteConfirmation = () => {
    this.setState({
      confirmDeleteModalOpen: false,
      imagePendingDelete: null,
    });
  };

  closeImagePreviewModal = () => {
    this.setState({
      previewImageUrl: "",
      previewModalOpen: false,
    });
  };

  extractRemarksValue = (remarks) => {
    if (!remarks) return "";
    if (typeof remarks === "string") {
      return remarks.toUpperCase();
    }
    if (typeof remarks === "object") {
      if (remarks.text) return remarks.text.toUpperCase();
      if (remarks.title) return remarks.title.toUpperCase();
      if (remarks.id) return remarks.id.toUpperCase();
      if (remarks.key) return remarks.key.toUpperCase();
      if (remarks.value) return remarks.value.toUpperCase();
    }
    return String(remarks).toUpperCase();
  };

  checkShouldShowImageInput = (remarks) => {
    const remarksValue = this.extractRemarksValue(remarks);
    return remarksValue === "DAMAGED" || remarksValue === "DEFECTIVE";
  };

  shouldUploadImages = () => {
    return this.state.selectedFiles.length > 0;
  };

  extractOrderPid = (response) => {
    if (response?.data?.id) return response.data.id;
    if (response?.data?.pid) return response.data.pid;
    if (response?.data?.metadata?.pid) return response.data.metadata.pid;
    if (response?.id) return response.id;
    if (response?.pid) return response.pid;
    return null;
  };

  submitAction = async (formData) => {
    const {
      match: {
        params: { orderPid },
      },
    } = this.props;

    const isEditing = !!orderPid;
    const { selectedFiles, existingImages } = this.state;

    // Validation: Check if status is CANCELLED but cancel_reason is missing
    if (formData.status === "CANCELLED" && !formData.cancel_reason?.trim()) {
      const error = new Error("Validation failed");
      error.response = {
        data: {
          errors: [
            {
              field: "cancel_reason",
              message: "is a required property",
            },
          ],
        },
      };
      error.skipFormError = false;
      throw error;
    }

    // Validation: Check if remarks is DAMAGED or DEFECTIVE but notes is missing
    if (
      (formData.remarks === "DAMAGED" || formData.remarks === "DEFECTIVE") &&
      !formData.notes?.trim()
    ) {
      const error = new Error("Validation failed");
      error.response = {
        data: {
          errors: [
            {
              field: "notes",
              message: "is a required property",
            },
          ],
        },
      };
      error.skipFormError = false;
      throw error;
    }

    // Validation: Check if status is RECEIVED but received_date is missing
    if (formData.status === "RECEIVED" && !formData.received_date?.trim()) {
      const error = new Error("Validation failed");
      error.response = {
        data: {
          errors: [
            {
              field: "received_date",
              message: "is a required property",
            },
          ],
        },
      };
      error.skipFormError = false;
      throw error;
    }

    const userRemovedAllImages = existingImages.length === 0;
    const userAddedImages = selectedFiles.length > 0;

    let finalImageFilenames;
    if (isEditing) {
      if (userRemovedAllImages && !userAddedImages) {
        finalImageFilenames = [];
      } else if (!userRemovedAllImages && !userAddedImages) {
        finalImageFilenames = existingImages;
      } else {
        finalImageFilenames = [...existingImages];
      }
    } else {
      finalImageFilenames = [];
    }

    const completeFormData = {
      ...formData,
      order_lines: formData.order_lines || [],
      image_filenames: finalImageFilenames,
    };

    try {
      const response = isEditing
        ? await orderApi.update(orderPid, completeFormData)
        : await orderApi.create(completeFormData);

      // Audit logging after successful order operation
      const finalOrderPid = this.extractOrderPid(response) || orderPid;

      if (isEditing) {
        // Generate change details for editing
        const changes = this.generateChangeDetails(
          this.state.originalFormData,
          completeFormData
        );
        await this.logOrderAudit(finalOrderPid, changes, false);
      } else {
        // Log order creation
        await this.logOrderAudit(finalOrderPid, [], true);
      }

      return response;
    } catch (error) {
      this.setState({ error });
      throw error;
    }
  };

  uploadImageFiles = async (pid) => {
    const { selectedFiles, existingImages } = this.state;
    if (!pid) throw new Error("Order PID is required for image upload");

    const uploadPromises = selectedFiles.map(async (file) => {
      try {
        this.setState((prevState) => ({
          uploadProgress: {
            ...prevState.uploadProgress,
            [file.id]: { status: "uploading", progress: 0 },
          },
        }));

        const response = await orderApi.uploadImage(pid, file, existingImages);

        this.setState((prevState) => ({
          uploadProgress: {
            ...prevState.uploadProgress,
            [file.id]: { status: "completed", progress: 100 },
          },
        }));
        return response;
      } catch (error) {
        this.setState((prevState) => ({
          uploadProgress: {
            ...prevState.uploadProgress,
            [file.id]: { status: "failed", progress: 0 },
          },
        }));
        throw error;
      }
    });

    await Promise.allSettled(uploadPromises);

    this.setState({
      selectedFiles: [],
      uploadProgress: {},
    });

    const fileInput = document.getElementById("image-upload");
    if (fileInput) fileInput.value = "";
  };

  attachCreatedOrderToDocumentRequest = async (
    orderPid,
    documentRequestPid
  ) => {
    this.cancellableAttachOrder = withCancel(
      documentRequestApi.addProvider(documentRequestPid, {
        physical_item_provider: {
          pid: orderPid,
          pid_type:
            invenioConfig.DOCUMENT_REQUESTS.physicalItemProviders.acq.pid_type,
        },
      })
    );
    await this.cancellableAttachOrder.promise;
  };

  successCallback = async (response) => {
    const {
      match: {
        params: { orderPid },
      },
    } = this.props;
    const isEditing = !!orderPid;
    const finalOrderPid = this.extractOrderPid(response) || orderPid;
    if (!finalOrderPid) return;

    if (
      this.shouldUploadImages() &&
      !(this.backupUploadAttempted && !isEditing)
    ) {
      try {
        await this.uploadImageFiles(finalOrderPid);
      } catch (error) {
        console.warn("Image upload failed silently:", error);
      }
    }

    this.backupUploadAttempted = false;

    const extraData = _get(this.props, "location.state.extraData", {});
    const shouldAttachCreatedOrderToDocumentRequest = _get(
      extraData,
      "attachCreatedOrderToDocumentRequest",
      false
    );

    if (shouldAttachCreatedOrderToDocumentRequest && finalOrderPid) {
      const documentRequestPid = extraData.documentRequestPid;
      try {
        await this.attachCreatedOrderToDocumentRequest(
          finalOrderPid,
          documentRequestPid
        );
        goTo(BackOfficeRoutes.documentRequestDetailsFor(documentRequestPid));
      } catch (error) {
        goTo(AcquisitionRoutes.orderDetailsFor(finalOrderPid));
      }
    } else if (finalOrderPid) {
      goTo(AcquisitionRoutes.orderDetailsFor(finalOrderPid));
    }
  };

  renderCompactImageUploadSection = () => {
    const { selectedFiles, uploadProgress, existingImages } = this.state;
    const {
      match: {
        params: { orderPid },
      },
    } = this.props;
    const isEditing = !!orderPid;

    return (
      <Segment size="small" style={{ padding: "10px" }}>
        <Grid>
          <Grid.Row verticalAlign="middle">
            <Grid.Column width={8}>
              <Form.Field>
                <label style={{ fontSize: "0.9em" }}>
                  <Icon name="file image" />
                  {isEditing ? "Upload Evidence Images" : "Upload Images"}
                </label>
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  multiple
                  onChange={this.handleFileChange}
                  style={{ fontSize: "0.85em" }}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {selectedFiles.length > 0 && (
          <Message size="small" style={{ marginTop: "8px" }}>
            <div style={{ fontSize: "0.85em" }}>
              <Icon name="list" />
              <strong>Selected: {selectedFiles.length} file(s)</strong>
              {selectedFiles.map((file) => (
                <Label
                  key={file.id}
                  size="mini"
                  style={{ marginLeft: "5px" }}
                  color={
                    uploadProgress[file.id]?.status === "completed"
                      ? "green"
                      : uploadProgress[file.id]?.status === "failed"
                      ? "red"
                      : uploadProgress[file.id]?.status === "uploading"
                      ? "yellow"
                      : "blue"
                  }
                >
                  {file.name}
                  {!uploadProgress[file.id] && (
                    <Icon
                      name="close"
                      onClick={() => this.removeSelectedFile(file.id)}
                      style={{ cursor: "pointer", marginLeft: "3px" }}
                    />
                  )}
                </Label>
              ))}
            </div>
          </Message>
        )}

        {isEditing && existingImages.length > 0 && (
          <>
            <Message size="small" style={{ marginTop: "8px" }}>
              <div style={{ fontSize: "1em" }}>
                <Icon name="image" />
                <strong>Existing Images:</strong>
                <br />
                {existingImages.map((imageName, index) => (
                  <Label
                    key={index}
                    size="medium"
                    style={{ margin: "5px 5px 0 0" }}
                    color="teal"
                  >
                    <span
                      onClick={() => this.openImagePreviewModal(imageName)}
                      style={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "white",
                      }}
                      title="Click to preview image"
                    >
                      {imageName.split("-").pop()}
                    </span>
                    <Icon
                      name="trash alternate"
                      onClick={() => this.openDeleteConfirmation(imageName)}
                      style={{ cursor: "pointer", marginLeft: "5px" }}
                      title="Remove this image"
                    />
                  </Label>
                ))}
              </div>
            </Message>

            <Modal
              open={this.state.previewModalOpen}
              onClose={this.closeImagePreviewModal}
              size="mini"
              closeIcon
            >
              <Header icon="image" content="Image Preview" />
              <Modal.Content image style={{ textAlign: "center" }}>
                <img
                  src={this.state.previewImageUrl}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "50vh",
                    objectFit: "contain",
                  }}
                />
              </Modal.Content>
            </Modal>

            <Modal
              open={this.state.confirmDeleteModalOpen}
              onClose={this.closeDeleteConfirmation}
              size="tiny"
            >
              <Header icon="trash alternate" content="Confirm Image Deletion" />
              <Modal.Content>
                <p>
                  Are you sure you want to delete{" "}
                  <strong>
                    {this.state.imagePendingDelete &&
                      this.state.imagePendingDelete.split("/").pop()}
                  </strong>
                  ?
                </p>
              </Modal.Content>
              <Modal.Actions>
                <Button onClick={this.closeDeleteConfirmation}>
                  <Icon name="cancel" /> Cancel
                </Button>
                <Button negative onClick={this.removeExistingImage}>
                  <Icon name="trash alternate" /> Delete
                </Button>
              </Modal.Actions>
            </Modal>
          </>
        )}

        <Message size="mini" style={{ marginBottom: "0", fontSize: "0.75em" }}>
          <Icon name="info circle" />
          Supported: PNG, JPG, JPEG, GIF. Max: 16MB per file.
        </Message>
      </Segment>
    );
  };

  render() {
    const {
      match: {
        params: { orderPid },
      },
    } = this.props;
    const { isLoading, error, formData } = this.state;
    const isEditing = !!orderPid;
    const formTitle = isEditing
      ? `Received Document Records - Edit #${orderPid}`
      : "Received Document Records - Create";

    return (
      <Container>
        <Loader isLoading={isLoading}>
          <Error error={error}>
            <Segment>
              <Header as="h2" dividing>
                <Icon name={isEditing ? "edit" : "plus"} />
                {formTitle}
              </Header>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <RJSForm
                      schema={schema()}
                      uiSchema={uiSchema(formTitle)}
                      formData={formData}
                      onChange={this.onFormChange}
                      submitAction={this.submitAction}
                      successCallback={this.successCallback}
                      successMessage={`The acquisition order was successfully ${
                        isEditing ? "updated" : "created"
                      }.`}
                    />
                    {this.renderCompactImageUploadSection()}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          </Error>
        </Loader>
      </Container>
    );
  }
}

OrderEditor.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      orderPid: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      formData: PropTypes.object,
      extraData: PropTypes.object,
    }),
  }),
};

OrderEditor.defaultProps = {
  location: {
    state: null,
  },
};
